import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Form, FormField, FormSubmitHandler, OptionType} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {IssuedType} from '@dms/api/core';
import {useCreateTaxDocumentSettingMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

type FormValues = {
  branchId: string;
  issuedType: IssuedType;
};

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.accountingSettings.labels.issuedType'),
    href: settingsRoutes.taxDocumentIssuedType,
  },
];

export function TaxDocumentForPaymentIssuedTypeNew() {
  const navigate = useNavigate();
  const [createIssuedTypeDefinition] = useCreateTaxDocumentSettingMutation();

  const {branchOptions, isLoading, isError} = useBranches();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await createIssuedTypeDefinition({taxDocumentSettingRequestBody: data})
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.taxDocumentIssuedType);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const title = i18n.t('page.accounting.labels.newDefinition');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs,
        }}
        data-testid={testIds.settings.taxDocumentIssuedTypeNew('page')}
        isLoading={isLoading}
        isError={isError}
        isCreating
      >
        <Form<FormValues>
          onSubmit={handleSubmit}
          experimentalZodSchema={schema}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <SettingsSection>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="choice"
                    name="branchId"
                    isNotClearable
                    options={branchOptions}
                    label={i18n.t('entity.cashRegister.parameters.branch')}
                    isRequired
                    data-testid={testIds.settings.taxDocumentIssuedTypeNew('branch')}
                  />
                  <FormField
                    control={control}
                    type="choice"
                    options={issuedTypeOptions}
                    name="issuedType"
                    isRequired
                    label={i18n.t('page.accountingSettings.labels.issuedType')}
                    data-testid={testIds.settings.taxDocumentIssuedTypeNew('issuedType')}
                  />
                </Grid>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.taxDocumentIssuedType),
                    variant: 'secondary',
                    'data-testid': testIds.settings.taxDocumentIssuedTypeNew('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                    'data-testid': testIds.settings.taxDocumentIssuedTypeNew('save'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}

const schema = z.object({
  branchId: z.string(),
  issuedType: z.enum(['automatic', 'manual', 'not_create'] as const),
});

const issuedTypeOptions: OptionType<IssuedType>[] = [
  {value: 'automatic', label: i18n.t('page.accountingSettings.labels.automatic')},
  {value: 'manual', label: i18n.t('page.accountingSettings.labels.manual')},
  {value: 'not_create', label: i18n.t('page.accountingSettings.labels.not_create')},
];
